import type {
  DeleteOrganizationInvitationRequest,
  InviteUserToOrganizationRequest,
  ListOrganizationInvitationsRequest,
} from '@compass/client/types/organization';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { organizationService } from '@/services/api';

import { queryKeys as programUsersQueryKeys } from './useProgramUsers';

const queryKeys = {
  organizationInvitations: {
    all: ['compass', 'organizationInvitations'] as const,
    list: (organizationId: string, params?: Partial<ListOrganizationInvitationsRequest>) =>
      [...queryKeys.organizationInvitations.all, 'list', organizationId, params] as const,
  },
};

/**
 * Hook to list pending invitations for an organization
 */
export const useOrganizationInvitations = (
  organizationId?: string,
  params?: Omit<ListOrganizationInvitationsRequest, 'organization_id'>,
) => {
  return useQuery({
    queryKey: queryKeys.organizationInvitations.list(organizationId || '', params),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      return organizationService.listOrganizationInvitations({
        organization_id: organizationId,
        ...params,
      });
    },
    enabled: !!organizationId,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to invite a user to an organization (creates user if needed)
 */
export const useInviteUserToOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: InviteUserToOrganizationRequest) =>
      organizationService.inviteUserToOrganization(request),
    onSuccess: (_data, variables) => {
      // Invalidate invitations list
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizationInvitations.list(variables.organization_id || '', {}),
      });
      // Invalidate organization users list to refresh members
      queryClient.invalidateQueries({
        queryKey: ['compass', 'organizationUsers', 'search', variables.organization_id],
      });

      // If program_role_ids exists, invalidate program users for those programs
      if (variables.program_role_ids) {
        Object.keys(variables.program_role_ids).forEach(programId => {
          queryClient.invalidateQueries({
            queryKey: programUsersQueryKeys.programUsers.search(
              variables.organization_id || '',
              programId,
            ),
          });
        });
      }
    },
  });
};

/**
 * Hook to delete/revoke an invitation
 */
export const useDeleteOrganizationInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: DeleteOrganizationInvitationRequest) =>
      organizationService.deleteOrganizationInvitation(request),
    onSuccess: (_data, variables) => {
      // Invalidate invitations list
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizationInvitations.list(variables.organization_id || '', {}),
      });
    },
  });
};
